
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Filter } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { examAssignmentsAPI } from "@/services/api";
import MonthlyCalendarView from "@/components/admin/MonthlyCalendarView";
import WeeklyCalendarView from "@/components/admin/WeeklyCalendarView";
import ModernPageTemplate from "@/components/common/ModernPageTemplate";

const ExamCalendar = () => {
  const { toast } = useToast();
  const [activeView, setActiveView] = useState("month");
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Get exam assignments data for refresh functionality
  const { refetch } = useQuery({
    queryKey: ['exam-assignments-calendar', scheduleFilter],
    queryFn: () => examAssignmentsAPI.getAll({ 
      limit: 10000,
      myLimit: 10000,
      otherLimit: 10000
    }),
  });

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showFilterModal) {
        setShowFilterModal(false);
      }
    };

    if (showFilterModal) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showFilterModal]);

  const handleRefresh = async () => {
    try {
      await refetch();
      toast({
        title: "Calendar refreshed",
        description: "Exam schedule data has been updated.",
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Failed to refresh calendar data.",
        variant: "destructive",
      });
    }
  };

  const handleFilterClick = () => {
    setShowFilterModal(!showFilterModal);
  };

  const headerAction = null; // Remove header action

  const statsSection = (
    <div className="admin-calendar-header-tabs-stats">
      <TabsList className="admin-calendar-header-tabs-list">
        <TabsTrigger value="week" className="admin-calendar-header-tab">
          Weekly View
        </TabsTrigger>
        <TabsTrigger value="month" className="admin-calendar-header-tab">
          Monthly View
        </TabsTrigger>
      </TabsList>
    </div>
  );

  return (
    <Tabs value={activeView} onValueChange={setActiveView} className="admin-calendar-tabs-wrapper">
      <ModernPageTemplate
        title="Exam Schedule Calendar"
        headerAction={headerAction}
        stats={statsSection}
        onRefresh={handleRefresh}
        filter={{
          onFilterClick: handleFilterClick,
          isActive: showFilterModal
        }}
        contentClassName="admin-calendar-content-no-gap"
      >
        {/* Filter Modal */}
        {showFilterModal && (
          <div 
            className="admin-filter-overlay"
            onClick={(e) => {
              // Close modal when clicking on the overlay background
              if (e.target === e.currentTarget) {
                setShowFilterModal(false);
              }
            }}
          >
            <div className="admin-filter-modal">
              <div className="admin-filter-header">
                <h3>Filter Schedules</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilterModal(false)}
                >
                  ×
                </Button>
              </div>
              <div className="admin-filter-content">
                <div className="admin-filter-group">
                  <label>Schedule Type</label>
                  <Select value={scheduleFilter} onValueChange={setScheduleFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter schedules" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Schedules</SelectItem>
                      <SelectItem value="my">My Schedules</SelectItem>
                      <SelectItem value="company">Company Schedules</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="admin-filter-footer">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setScheduleFilter("all");
                  }}
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setShowFilterModal(false);
                    refetch();
                  }}
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Views */}
        <div className="admin-calendar-container">
          <TabsContent value="week" className="admin-calendar-tab-content">
            <WeeklyCalendarView scheduleFilter={scheduleFilter} />
          </TabsContent>
          
          <TabsContent value="month" className="admin-calendar-tab-content">
            <MonthlyCalendarView scheduleFilter={scheduleFilter} />
          </TabsContent>
        </div>
      </ModernPageTemplate>
    </Tabs>
  );
};

export default ExamCalendar;
